import { motion } from "framer-motion";
import { Shield, Lock, Eye, FileText, Phone, Mail, ChevronDown } from "lucide-react";
import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useCookieContext } from "@/contexts/CookieContext";
import { useTranslation } from "@/hooks/useTranslation";
// Helper function to render text with email links
const renderWithEmailLinks = (text: string) => {
  // Match all email addresses
  const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  const parts = text.split(emailRegex);
  
  return parts.map((part, index) => {
    if (emailRegex.test(part)) {
      // Reset regex lastIndex after test
      emailRegex.lastIndex = 0;
      return (
        <a 
          key={index}
          href={`mailto:${part}`}
          className="text-primary hover:text-primary/80 underline transition-colors"
        >
          {part}
        </a>
      );
    }
    // Check again with a fresh regex for the test
    if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(part)) {
      return (
        <a 
          key={index}
          href={`mailto:${part}`}
          className="text-primary hover:text-primary/80 underline transition-colors"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

const PersonalData = () => {
  const { t } = useTranslation();
  const {
    openSettings
  } = useCookieContext();
  const dataTypes = [{
    icon: Eye,
    title: "Useful Definitions",
    description: "Key terms and definitions for data protection"
  }, {
    icon: Shield,
    title: "Data Processing",
    description: "Information about what data we collect and how we use it"
  }, {
    icon: Lock,
    title: "Your Rights",
    description: "GDPR compliance - your rights regarding personal data"
  }, {
    icon: FileText,
    title: "Security & Policies",
    description: "Our security measures and compliance policies"
  }];
  return <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50 relative overflow-hidden">
      {/* Flowing background elements */}
      <div className="absolute top-0 right-0 w-1/2 h-[60%] bg-gradient-to-bl from-secondary/15 via-secondary/8 to-transparent blur-3xl" />
      <div className="absolute top-1/4 left-0 w-1/3 h-[50%] bg-gradient-to-r from-accent-teal/12 via-accent-teal/6 to-transparent blur-2xl" />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-[40%] bg-gradient-to-t from-secondary/10 via-transparent to-transparent blur-3xl" />
      
      <Navigation />
      <main className="pt-32 pb-16 relative overflow-hidden">
        
        {/* Floating privacy icons */}
        <motion.div className="absolute top-1/4 right-1/4 w-16 h-16 text-secondary/10" animate={{
        y: [0, -10, 0],
        rotate: [0, 5, 0]
      }} transition={{
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }}>
          <Shield className="w-full h-full" />
        </motion.div>
        
        <div className="container-padding relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Hero Section */}
            <motion.div className="text-center mb-20" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }}>
              <motion.div className="inline-flex items-center gap-2 bg-gradient-to-r from-secondary/10 to-accent-teal/10 
                           border border-secondary/20 rounded-full px-6 py-3 text-secondary font-medium
                           backdrop-blur-sm mb-6" initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }}>
                <Shield className="w-4 h-4" />
                {t.personalData.badge}
              </motion.div>
              
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.3
            }} className="heading-xl mb-8 text-accent-teal break-words hyphens-auto px-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight" style={{ wordBreak: 'break-word' }}>
                {t.personalData.title}
              </motion.h1>
              
              <motion.p className="text-xl text-neutral-700 max-w-3xl mx-auto leading-relaxed" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.4
            }}>
                {t.personalData.description}
              </motion.p>
            </motion.div>

            {/* Data Protection Overview */}
            

            {/* Main Content - Accordion */}
            <motion.div className="healthcare-card group mb-20" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.8
          }}>
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent-teal/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <Accordion type="single" collapsible className="w-full space-y-4">
                  
                  {/* Useful Definitions */}
                  <AccordionItem value="definitions" className="border border-neutral-200 rounded-xl px-6">
                    <AccordionTrigger className="text-base sm:text-xl font-bold text-primary hover:no-underline py-6">
                      <div className="flex items-center justify-center sm:justify-start gap-3 w-full">
                        <Eye className="hidden sm:block w-5 h-5 sm:w-6 sm:h-6 text-secondary flex-shrink-0" />
                        {t.personalData.sections.usefulDefinitions}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-neutral-700 leading-relaxed pb-6">
                      <p className="mb-4">{t.personalData.usefulDefinitionsContent.intro}</p>
                      
                      <div className="space-y-4">
                        <div>
                          <p>{t.personalData.usefulDefinitionsContent.personalData}</p>
                        </div>
                        
                        <div>
                          <p>{t.personalData.usefulDefinitionsContent.sensitiveData}</p>
                        </div>
                        
                        <div>
                          <p>{t.personalData.usefulDefinitionsContent.dataProcessing}</p>
                        </div>
                        
                        <div>
                          <p>{t.personalData.usefulDefinitionsContent.legislation}</p>
                        </div>
                        
                        <div>
                          <p>{t.personalData.usefulDefinitionsContent.dataLogs}</p>
                        </div>
                        
                        <div>
                          <p>{t.personalData.usefulDefinitionsContent.cookies} <button onClick={openSettings} className="text-primary underline hover:text-primary/80 cursor-pointer bg-transparent border-none p-0 font-inherit">
                            {t.personalData.usefulDefinitionsContent.specificInformation}
                          </button> {t.personalData.usefulDefinitionsContent.cookiesEnd}</p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Data Controller */}
                  <AccordionItem value="controller" className="border border-neutral-200 rounded-xl px-6">
                    <AccordionTrigger className="text-base sm:text-xl font-bold text-primary hover:no-underline py-6">
                      <div className="flex items-center justify-center sm:justify-start gap-3 w-full">
                        <Shield className="hidden sm:block w-5 h-5 sm:w-6 sm:h-6 text-secondary flex-shrink-0" />
                        {t.personalData.sections.dataController}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-neutral-700 leading-relaxed pb-6">
                      <p>{renderWithEmailLinks(t.personalData.dataControllerContent.paragraph1)}</p>
                      <p className="mt-4">{renderWithEmailLinks(t.personalData.dataControllerContent.paragraph2)}</p>
                    </AccordionContent>
                  </AccordionItem>

                  {/* What Data We Process */}
                  <AccordionItem value="data-processing" className="border border-neutral-200 rounded-xl px-6">
                    <AccordionTrigger className="text-base sm:text-xl font-bold text-primary hover:no-underline py-6">
                      <div className="flex items-center justify-center sm:justify-start gap-3 w-full">
                        <FileText className="hidden sm:block w-5 h-5 sm:w-6 sm:h-6 text-secondary flex-shrink-0" />
                        {t.personalData.sections.whatDataWeProcess}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-neutral-700 leading-relaxed pb-6">
                      <h4 className="font-bold text-primary mb-3 text-sm sm:text-base">{t.personalData.whatDataWeProcessContent.sectionA}</h4>
                      
                      <div className="mb-6">
                        <h5 className="font-semibold mb-2">{t.personalData.whatDataWeProcessContent.dataLogs}</h5>
                        <p className="mb-3">{t.personalData.whatDataWeProcessContent.dataLogsIntro}</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          {t.personalData.whatDataWeProcessContent.dataLogsList.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="mb-6">
                        <h5 className="font-semibold mb-2">{t.personalData.whatDataWeProcessContent.cookiesSubtitle}</h5>
                        <p>{t.personalData.whatDataWeProcessContent.cookiesText}</p>
                      </div>

                      <h4 className="font-bold text-primary mb-3">{t.personalData.whatDataWeProcessContent.sectionB}</h4>
                      <p>{t.personalData.whatDataWeProcessContent.sectionBText}</p>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Cookies Information */}
                  <AccordionItem value="cookies" className="border border-neutral-200 rounded-xl px-6">
                    <AccordionTrigger className="text-base sm:text-xl font-bold text-primary hover:no-underline py-6">
                      <div className="flex items-center justify-center sm:justify-start gap-3 w-full">
                        <Lock className="hidden sm:block w-5 h-5 sm:w-6 sm:h-6 text-secondary flex-shrink-0" />
                        {t.personalData.sections.informationRegardingCookies}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-neutral-700 leading-relaxed pb-6">
                      <p className="mb-4">{t.personalData.cookiesInfoContent.intro}</p>
                      
                      <div className="mb-6">
                        <h5 className="font-semibold mb-2">{t.personalData.cookiesInfoContent.essentialTitle}</h5>
                        <p>{t.personalData.cookiesInfoContent.essentialText}</p>
                      </div>
                      
                      <div className="mb-6">
                        <h5 className="font-semibold mb-2">{t.personalData.cookiesInfoContent.analyticsTitle}</h5>
                        <p>{t.personalData.cookiesInfoContent.analyticsText}</p>
                      </div>

                      <div className="overflow-x-auto">
                          <table className="w-full border-collapse border border-neutral-300 text-xs sm:text-sm">
                          <thead>
                            <tr className="bg-neutral-100">
                              <th className="border border-neutral-300 p-2 sm:p-3 text-center sm:text-left">{t.personalData.cookiesInfoContent.tableHeaders.cookieName}</th>
                              <th className="border border-neutral-300 p-2 sm:p-3 text-center sm:text-left">{t.personalData.cookiesInfoContent.tableHeaders.category}</th>
                              <th className="border border-neutral-300 p-2 sm:p-3 text-center sm:text-left">{t.personalData.cookiesInfoContent.tableHeaders.purpose}</th>
                              <th className="border border-neutral-300 p-2 sm:p-3 text-center sm:text-left">{t.personalData.cookiesInfoContent.tableHeaders.duration}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {t.personalData.cookiesInfoContent.tableData.map((row, index) => (
                              <tr key={index}>
                                <td className="border border-neutral-300 p-2 sm:p-3 text-center sm:text-left">{row.name}</td>
                                <td className="border border-neutral-300 p-2 sm:p-3 text-center sm:text-left">{row.category}</td>
                                <td className="border border-neutral-300 p-2 sm:p-3 text-center sm:text-left">{row.purpose}</td>
                                <td className="border border-neutral-300 p-2 sm:p-3 text-center sm:text-left">{row.duration}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="mt-6 p-4 bg-accent-teal/10 rounded-lg">
                        <h6 className="font-semibold mb-2">{t.personalData.cookiesInfoContent.managingTitle}</h6>
                        <p>{t.personalData.cookiesInfoContent.managingText}</p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Your Rights */}
                  <AccordionItem value="rights" className="border border-neutral-200 rounded-xl px-6">
                    <AccordionTrigger className="text-base sm:text-xl font-bold text-primary hover:no-underline py-6">
                      <div className="flex items-center justify-center sm:justify-start gap-3 w-full">
                        <Shield className="hidden sm:block w-5 h-5 sm:w-6 sm:h-6 text-secondary flex-shrink-0" />
                        {t.personalData.sections.yourRights}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-neutral-700 leading-relaxed pb-6">
                      <p className="mb-4">{t.personalData.yourRightsContent.intro}</p>
                      
                      <ol className="list-decimal list-inside space-y-3">
                        {t.personalData.yourRightsContent.rights.map((right, index) => (
                          <li key={index}>{right}</li>
                        ))}
                      </ol>
                      
                      <div className="mt-6 p-4 bg-neutral-50 rounded-lg">
                        <h5 className="font-semibold text-primary mb-2">{t.personalData.yourRightsContent.contactTitle}</h5>
                        <p>{renderWithEmailLinks(t.personalData.yourRightsContent.contactText1)}</p>
                        <p className="mt-2">{renderWithEmailLinks(t.personalData.yourRightsContent.contactText2)}</p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Data Storage Period */}
                  <AccordionItem value="storage" className="border border-neutral-200 rounded-xl px-6">
                    <AccordionTrigger className="text-base sm:text-xl font-bold text-primary hover:no-underline py-6">
                      <div className="flex items-center justify-center sm:justify-start gap-3 w-full">
                        <FileText className="hidden sm:block w-5 h-5 sm:w-6 sm:h-6 text-secondary flex-shrink-0" />
                        {t.personalData.sections.periodOfStorage}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-neutral-700 leading-relaxed pb-6">
                      <div className="space-y-4">
                        <p><strong>IP address:</strong> {t.personalData.periodOfStorageContent.ipAddress}</p>
                        
                        <p><strong>Session-Cookies:</strong> {t.personalData.periodOfStorageContent.sessionCookies}</p>
                        
                        <p><strong>Data:</strong> {t.personalData.periodOfStorageContent.emailData}</p>
                        
                        <p>{t.personalData.periodOfStorageContent.claimsData}</p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Information Security Policy */}
                  <AccordionItem value="security" className="border border-neutral-200 rounded-xl px-6">
                    <AccordionTrigger className="text-base sm:text-xl font-bold text-primary hover:no-underline py-6">
                      <div className="flex items-center justify-center sm:justify-start gap-3 w-full break-words hyphens-auto px-2" style={{ wordBreak: 'break-word' }}>
                        <Lock className="hidden sm:block w-5 h-5 sm:w-6 sm:h-6 text-secondary flex-shrink-0" />
                        {t.personalData.sections.informationSecurityPolicy}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-neutral-700 leading-relaxed pb-6">
                      <p className="mb-4">{t.personalData.informationSecurityPolicyContent.intro}</p>
                      
                      <div className="mb-6">
                        <h5 className="font-semibold text-primary mb-2">{t.personalData.informationSecurityPolicyContent.coversTitle}</h5>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          {t.personalData.informationSecurityPolicyContent.coversList.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="mb-6">
                        <h5 className="font-semibold text-primary mb-2">{t.personalData.informationSecurityPolicyContent.objectivesTitle}</h5>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          {t.personalData.informationSecurityPolicyContent.objectivesList.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="mt-6 p-4 bg-neutral-50 rounded-lg">
                        <p className="font-semibold">{t.personalData.informationSecurityPolicyContent.managingDirector}</p>
                        <p>{t.personalData.informationSecurityPolicyContent.name}</p>
                        <p className="text-sm text-neutral-600 mt-2">{t.personalData.informationSecurityPolicyContent.date}</p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Purposes and legal basis for processing your data */}
                  <AccordionItem value="purposes-legal-basis" className="border border-neutral-200 rounded-xl px-6">
                    <AccordionTrigger className="text-base sm:text-xl font-bold text-primary hover:no-underline py-6">
                      <div className="flex items-center justify-center sm:justify-start gap-3 w-full">
                        <FileText className="hidden sm:block w-5 h-5 sm:w-6 sm:h-6 text-secondary flex-shrink-0" />
                        {t.personalData.sections.purposesAndLegalBasis}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-neutral-700 leading-relaxed pb-6">
                      <p className="mb-4">{t.personalData.purposesAndLegalBasisContent.paragraph1}</p>
                      
                      <p>{t.personalData.purposesAndLegalBasisContent.paragraph2}</p>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Links of third parties' websites */}
                  <AccordionItem value="third-party-links" className="border border-neutral-200 rounded-xl px-6">
                    <AccordionTrigger className="text-base sm:text-xl font-bold text-primary hover:no-underline py-6">
                      <div className="flex items-center justify-center sm:justify-start gap-3 w-full">
                        <Eye className="hidden sm:block w-5 h-5 sm:w-6 sm:h-6 text-secondary flex-shrink-0" />
                        {t.personalData.sections.thirdPartyLinks}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-neutral-700 leading-relaxed pb-6">
                      <p>{t.personalData.thirdPartyLinksContent}</p>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Revisions */}
                  <AccordionItem value="revisions" className="border border-neutral-200 rounded-xl px-6">
                    <AccordionTrigger className="text-base sm:text-xl font-bold text-primary hover:no-underline py-6">
                      <div className="flex items-center justify-center sm:justify-start gap-3 w-full">
                        <FileText className="hidden sm:block w-5 h-5 sm:w-6 sm:h-6 text-secondary flex-shrink-0" />
                        {t.personalData.sections.revisions}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-neutral-700 leading-relaxed pb-6">
                      <p>{t.personalData.revisionsContent}</p>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Anti-bribery policy */}
                  <AccordionItem value="anti-bribery" className="border border-neutral-200 rounded-xl px-6">
                    <AccordionTrigger className="text-base sm:text-xl font-bold text-primary hover:no-underline py-6">
                      <div className="flex items-center justify-center sm:justify-start gap-3 w-full">
                        <Shield className="hidden sm:block w-5 h-5 sm:w-6 sm:h-6 text-secondary flex-shrink-0" />
                        {t.personalData.sections.antiBriberyPolicy}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-neutral-700 leading-relaxed pb-6">
                      <div className="space-y-6">
                        <div>
                          <h4 className="font-bold text-primary mb-3 text-xs sm:text-sm md:text-base px-2 break-words leading-tight" style={{ wordBreak: 'break-word' }}>{t.personalData.antiBriberyPolicyContent.policySummary.title}</h4>
                          <p>{renderWithEmailLinks(t.personalData.antiBriberyPolicyContent.policySummary.intro)}</p>
                          <p className="mt-2">{t.personalData.antiBriberyPolicyContent.policySummary.conjunction}</p>
                        </div>

                        <div>
                          <h4 className="font-bold text-primary mb-3">{t.personalData.antiBriberyPolicyContent.purposeAndObjectives.title}</h4>
                          <p>{t.personalData.antiBriberyPolicyContent.purposeAndObjectives.content}</p>
                        </div>

                        <div>
                          <h4 className="font-bold text-primary mb-3">{t.personalData.antiBriberyPolicyContent.risksAndConsequences.title}</h4>
                          <p>{t.personalData.antiBriberyPolicyContent.risksAndConsequences.content}</p>
                        </div>

                        <div>
                          <h4 className="font-bold text-primary mb-3">{t.personalData.antiBriberyPolicyContent.scopeAndRange.title}</h4>
                          <p>{t.personalData.antiBriberyPolicyContent.scopeAndRange.content}</p>
                        </div>

                        <div>
                          <h4 className="font-bold text-primary mb-3">{t.personalData.antiBriberyPolicyContent.policyDetails.title}</h4>
                          
                          <div className="space-y-4">
                            <div>
                              <h5 className="font-semibold mb-2">{t.personalData.antiBriberyPolicyContent.policyDetails.applicableLaws.title}</h5>
                              <p>{t.personalData.antiBriberyPolicyContent.policyDetails.applicableLaws.content}</p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">{t.personalData.antiBriberyPolicyContent.policyDetails.prohibitionOfBribery.title}</h5>
                              
                              <div className="ml-4 space-y-3">
                                <div>
                                  <h6 className="font-medium">{t.personalData.antiBriberyPolicyContent.policyDetails.prohibitionOfBribery.governmentBribery.title}</h6>
                                  <p>{t.personalData.antiBriberyPolicyContent.policyDetails.prohibitionOfBribery.governmentBribery.content}</p>
                                </div>

                                <div>
                                  <h6 className="font-medium">{t.personalData.antiBriberyPolicyContent.policyDetails.prohibitionOfBribery.commercialBribery.title}</h6>
                                  <p>{t.personalData.antiBriberyPolicyContent.policyDetails.prohibitionOfBribery.commercialBribery.content}</p>
                                </div>

                                <div>
                                  <h6 className="font-medium">{t.personalData.antiBriberyPolicyContent.policyDetails.prohibitionOfBribery.facilitationPayments.title}</h6>
                                  <p>{t.personalData.antiBriberyPolicyContent.policyDetails.prohibitionOfBribery.facilitationPayments.content}</p>
                                </div>
                              </div>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">{t.personalData.antiBriberyPolicyContent.policyDetails.provisionOfItems.title}</h5>
                              
                              <div className="ml-4 space-y-3">
                                <div>
                                  <h6 className="font-medium">{t.personalData.antiBriberyPolicyContent.policyDetails.provisionOfItems.governmentTransactionApproval.title}</h6>
                                  <p>{t.personalData.antiBriberyPolicyContent.policyDetails.provisionOfItems.governmentTransactionApproval.content}</p>
                                </div>

                                <div>
                                  <h6 className="font-medium">{t.personalData.antiBriberyPolicyContent.policyDetails.provisionOfItems.giftsMealsTravel.title}</h6>
                                  <p>{t.personalData.antiBriberyPolicyContent.policyDetails.provisionOfItems.giftsMealsTravel.content}</p>
                                </div>

                                <div>
                                  <h6 className="font-medium">{t.personalData.antiBriberyPolicyContent.policyDetails.provisionOfItems.gifts.title}</h6>
                                  <p>{t.personalData.antiBriberyPolicyContent.policyDetails.provisionOfItems.gifts.content}</p>
                                </div>

                                <div>
                                  <h6 className="font-medium">{t.personalData.antiBriberyPolicyContent.policyDetails.provisionOfItems.promotionDemonstration.title}</h6>
                                  <p>{t.personalData.antiBriberyPolicyContent.policyDetails.provisionOfItems.promotionDemonstration.content}</p>
                                </div>

                                <div>
                                  <h6 className="font-medium">{t.personalData.antiBriberyPolicyContent.policyDetails.provisionOfItems.hiringGovernmentOfficials.title}</h6>
                                  <p>{t.personalData.antiBriberyPolicyContent.policyDetails.provisionOfItems.hiringGovernmentOfficials.content}</p>
                                </div>

                                <div>
                                  <h6 className="font-medium">{t.personalData.antiBriberyPolicyContent.policyDetails.provisionOfItems.politicalContributions.title}</h6>
                                  <p>{t.personalData.antiBriberyPolicyContent.policyDetails.provisionOfItems.politicalContributions.content}</p>
                                </div>
                              </div>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">{t.personalData.antiBriberyPolicyContent.policyDetails.thirdPartyManagement.title}</h5>
                              
                              <div className="ml-4 space-y-3">
                                <div>
                                  <h6 className="font-medium">{t.personalData.antiBriberyPolicyContent.policyDetails.thirdPartyManagement.applicability.title}</h6>
                                  <p>{t.personalData.antiBriberyPolicyContent.policyDetails.thirdPartyManagement.applicability.paragraph1}</p>
                                  <p className="mt-2">{t.personalData.antiBriberyPolicyContent.policyDetails.thirdPartyManagement.applicability.paragraph2}</p>
                                </div>

                                <div>
                                  <h6 className="font-medium">{t.personalData.antiBriberyPolicyContent.policyDetails.thirdPartyManagement.dueDiligence.title}</h6>
                                  <p>{t.personalData.antiBriberyPolicyContent.policyDetails.thirdPartyManagement.dueDiligence.paragraph1}</p>
                                  <p className="mt-2">{t.personalData.antiBriberyPolicyContent.policyDetails.thirdPartyManagement.dueDiligence.paragraph2}</p>
                                  <p className="mt-2">{t.personalData.antiBriberyPolicyContent.policyDetails.thirdPartyManagement.dueDiligence.paragraph3}</p>
                                  <p className="mt-2">{t.personalData.antiBriberyPolicyContent.policyDetails.thirdPartyManagement.dueDiligence.paragraph4}</p>
                                </div>
                              </div>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">{t.personalData.antiBriberyPolicyContent.policyDetails.education.title}</h5>
                              <p>{t.personalData.antiBriberyPolicyContent.policyDetails.education.paragraph1}</p>
                              <p className="mt-2">{t.personalData.antiBriberyPolicyContent.policyDetails.education.paragraph2}</p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">{t.personalData.antiBriberyPolicyContent.policyDetails.audit.title}</h5>
                              <p>{t.personalData.antiBriberyPolicyContent.policyDetails.audit.content}</p>
                            </div>

                            <div>
                              <h5 className="font-semibold mb-2">{t.personalData.antiBriberyPolicyContent.policyDetails.reportingViolations.title}</h5>
                              <p>{renderWithEmailLinks(t.personalData.antiBriberyPolicyContent.policyDetails.reportingViolations.content)}</p>
                            </div>
                          </div>
                        </div>

                        <div className="mt-6 text-right">
                          <p className="font-semibold">{t.personalData.antiBriberyPolicyContent.managingDirector}</p>
                          <p className="font-semibold">{t.personalData.antiBriberyPolicyContent.name}</p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                </Accordion>
              </div>
            </motion.div>

            {/* Contact Information */}
            
          </div>
        </div>
      </main>
      <Footer />
    </div>;
};
export default PersonalData;